import { ReactNode, useCallback, useMemo } from 'react';
import { getEngine } from '@lio/rule-engine';
import { PanelInstance } from '@store/Quote/Quote.types';
import { UIFieldType } from '@services/data/productModelTypes';
import { Item } from '@components/select/Select';
import { JurisdictionOption } from '@models/brokerageOffering';
import { PolicyCoverages } from '@models/insuranceLine';
import { setDateToMidnight } from '@utils/date';
import { useApiEnvStore } from '@utils/api-client';

import {
  DateField,
  QuotePercentField,
  TextboxField,
  CheckboxField,
  RadioField,
  SelectField,
  FileField,
  DocumentValue,
  DocumentListValue,
  FilteredSelectField,
} from '@components/quoteDetails/quoteFields';
import { AdditionalInfoLinkProps } from '@components/container/AdditionalInfoLink';
import { RadioButtonGroupField } from '@components/quoteDetails/quoteFields/RadioButtonGroupField';
import { Merge } from 'type-fest';
import { safeExecuteRuleEngine } from '@utils/safeRuleEngineExecute';

type PanelAttributeType = PanelInstance['quoteItem']['attributeTypes'][string];

type QFCType = {
  attributeType: PanelAttributeType;
  disabledPolicyCoverages: PolicyCoverages;
  errorMessage: string | undefined;
  externalIcon: ReactNode;
  filteredJurisdictions?: JurisdictionOption[];
  hoverText: string | ReactNode | null;
  isCollapsed: boolean;
  isOverriding: boolean;
  isReadOnly: boolean;
  quoteId: string;
  quoteItemId: string;
  quoteFormId?: string;
  ruleEngine: ReturnType<typeof getEngine>;
  setValue: (value: any) => void;
  uiFieldType: UIFieldType | null;
  value: any;
  panelInstanceId: string;
  additionalInfoLink?: AdditionalInfoLinkProps;
  deprecated?: boolean | null;
  onBlur?: () => void;
};

export type QFCTypeExecuted = Merge<
  QFCType,
  {
    attributeType: {
      label: string | null;
      helpText: string | null;
      deprecated: boolean | null;
    } & Omit<PanelAttributeType, 'label' | 'helpText'>;
  }
>;

export function QuoteFieldControl(props: QFCType) {
  const [debugMode, debugModeOptions] = useApiEnvStore((state) => [state.debugMode, state.debugModeOptions]);
  const showQuoteCodeNames = debugMode && debugModeOptions.showQuoteCodeNames;
  const {
    attributeType,
    isCollapsed,
    isReadOnly,
    quoteId,
    quoteItemId,
    quoteFormId,
    ruleEngine,
    setValue,
    uiFieldType,
    value,
    onBlur,
  } = props;
  const executeOptions = useMemo(() => (quoteFormId ? { quoteFormId } : undefined), [quoteFormId]);
  const isRequired = !!ruleEngine.execute(attributeType.isRequired, executeOptions);

  const isRequiredBeforeBind =
    !isRequired && !!ruleEngine.execute(attributeType.isRequiredBeforeBind ?? null, executeOptions);

  const handleBlur = useCallback(() => {
    if ((attributeType.uiFieldType as UIFieldType) === UIFieldType.Date) {
      if (
        value !== undefined &&
        value !== null &&
        value?.toString() !== 'Invalid Date' &&
        new Date(value as string).getFullYear() > 1000
      ) {
        setValue(setDateToMidnight(new Date(value as string)));
      } else {
        setValue(null);
      }
    }
    if (onBlur) {
      onBlur();
    }
  }, [value, setValue, attributeType, onBlur]);

  const getOptionValues = ({ isOverriding }: { isOverriding: boolean }): Item[] => {
    let options: Item[];
    // Forms V2 fields pass quoteItemId='' because form attributes aren't tied to a quote item.
    // Setting the engine's initial quote item to '' corrupts rule resolution for relative-path
    // args like `.LOIGeneralLiabilityInd`, which flips unrelated fields' isActive results.
    if (quoteItemId) {
      ruleEngine.setEngineInitialQuoteItem(quoteItemId);
    }

    if (isOverriding && attributeType.overrideOptionValues) {
      options = [
        ...((safeExecuteRuleEngine(
          ruleEngine,
          attributeType,
          'overrideOptionValues',
          executeOptions,
        ) as unknown as Item[]) ?? []),
      ];
    } else {
      options = [
        ...((safeExecuteRuleEngine(ruleEngine, attributeType, 'optionValues', executeOptions) as unknown as Item[]) ??
          []),
      ];
    }

    return options;
  };

  let additionalInfoLink = undefined;
  if (attributeType.additionalInfoLinkUrl && attributeType.additionalInfoLinkText) {
    const rawUrl = safeExecuteRuleEngine(ruleEngine, attributeType, 'additionalInfoLinkUrl', executeOptions);
    const rawText = safeExecuteRuleEngine(ruleEngine, attributeType, 'additionalInfoLinkText', executeOptions);

    if (typeof rawUrl === 'string' && typeof rawText === 'string') {
      additionalInfoLink = { url: rawUrl, text: rawText };
    }
  }

  const fieldProps = useMemo(() => {
    const helpText = safeExecuteRuleEngine(ruleEngine, attributeType, 'helpText', executeOptions) as string;
    const label = showQuoteCodeNames
      ? attributeType.codeName
      : (ruleEngine.execute(attributeType.label, executeOptions) as string);

    return {
      ...props,
      attributeType: {
        ...props.attributeType,
        label,
        helpText,
      },
    };
  }, [showQuoteCodeNames, attributeType, ruleEngine, props, executeOptions]);

  switch (uiFieldType as UIFieldType) {
    case UIFieldType.Percentage:
      return (
        <QuotePercentField
          {...fieldProps}
          isRequired={isRequired}
          isRequiredBeforeBind={isRequiredBeforeBind}
          onBlur={handleBlur}
          additionalInfoLink={additionalInfoLink}
        />
      );
    case UIFieldType.Textbox:
      return (
        <TextboxField
          {...fieldProps}
          isRequired={isRequired}
          isRequiredBeforeBind={isRequiredBeforeBind}
          onBlur={handleBlur}
          additionalInfoLink={additionalInfoLink}
        />
      );
    case UIFieldType.Checkbox:
      return (
        //  additionalInfoLink is not currently supported for checkboxes
        <CheckboxField
          {...fieldProps}
          isRequired={isRequired}
          isRequiredBeforeBind={isRequiredBeforeBind}
          onBlur={handleBlur}
        />
      );
    case UIFieldType.Radio:
      return (
        //  additionalInfoLink is not currently supported for radio fields
        <RadioField
          {...fieldProps}
          getOptionValues={getOptionValues}
          isRequired={isRequired}
          isRequiredBeforeBind={isRequiredBeforeBind}
          onBlur={handleBlur}
        />
      );
    case UIFieldType.Date:
      //  additionalInfoLink is not currently supported for date fields
      return (
        <DateField
          {...fieldProps}
          isRequired={isRequired}
          isRequiredBeforeBind={isRequiredBeforeBind}
          onBlur={handleBlur}
        />
      );
    case UIFieldType.RadioButtonGroup:
      return (
        <RadioButtonGroupField
          {...fieldProps}
          getOptionValues={getOptionValues}
          isRequired={isRequired}
          isRequiredBeforeBind={isRequiredBeforeBind}
          onBlur={handleBlur}
          additionalInfoLink={additionalInfoLink}
        />
      );
    case UIFieldType.Select:
      return (
        <SelectField
          {...fieldProps}
          getOptionValues={getOptionValues}
          isRequired={isRequired}
          isRequiredBeforeBind={isRequiredBeforeBind}
          onBlur={handleBlur}
          additionalInfoLink={additionalInfoLink}
        />
      );
    case UIFieldType.File:
      //  additionalInfoLink is not currently supported for file fields
      return (
        <FileField
          quoteId={quoteId}
          quoteItemId={quoteItemId}
          attributeType={fieldProps.attributeType}
          isCollapsed={isCollapsed}
          isDisabled={isReadOnly}
          value={value as DocumentValue | DocumentListValue | null | undefined}
        />
      );
    case UIFieldType.FilteredSelect:
      //  additionalInfoLink is not currently supported for checkboxes
      return (
        <FilteredSelectField
          {...fieldProps}
          getOptionValues={getOptionValues}
          isRequired={isRequired}
          isRequiredBeforeBind={isRequiredBeforeBind}
          onBlur={handleBlur}
          additionalInfoLink={additionalInfoLink}
        />
      );

    default: {
      console.warn('Missing UIFieldType for attributeType', attributeType.codeName, attributeType.uiFieldType);
      return null;
    }
  }
}
